/**
 * CaptureModeChooser — TASK-673.
 *
 * The explicit bridge from a scheduled meeting to capture. Before 673 a scheduled row had
 * no Start action and the recorder was reachable only by typing its URL; live rows always
 * summoned the passive bot monitor even for in-person meetings. This names the exact
 * meeting and makes the RM choose HOW it is captured, then performs one canonical,
 * RLS-scoped `POST /meeting/{id}/start` before routing to the matching surface.
 *
 * The capture paths are mutually exclusive by construction:
 *   - a bot-owned meeting (a conference URL is set) offers only "join & monitor" — the
 *     browser microphone is never offered, so audio is never double-captured;
 *   - an in-person meeting offers "record" (→ the silent recorder, which itself gates the
 *     mic behind consent) or "don't record — dictate after" (no microphone at all).
 *
 * No microphone is ever requested here — recording consent and `getUserMedia` live in the
 * recorder, reached only after "record". Starting is deliberate, never automatic.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Radio, X } from 'lucide-react';
import { toast } from 'sonner';

import { startMeeting } from '../../services/api/meetingApi';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useCanvasActions } from '../../stores/canvasStore';

export interface CaptureModeChooserProps {
  sessionId: string;
  clientId: string;
  clientName: string;
  meetingGoal?: string | null;
  /** Presence means a bot owns the audio — the browser microphone is prohibited. */
  meetingUrl?: string | null;
  onClose: () => void;
}

export function CaptureModeChooser({
  sessionId,
  clientId,
  clientName,
  meetingGoal,
  meetingUrl,
  onClose,
}: CaptureModeChooserProps) {
  const navigate = useNavigate();
  const { addSpecs } = useCanvasActions();
  const [busy, setBusy] = useState<string | null>(null);
  // Deep review #6: Escape closes, focus starts inside the dialog and returns to the opener.
  const dialogRef = useModalDismiss<HTMLDivElement>(onClose);
  const isBot = Boolean(meetingUrl);

  async function choose(
    mode: 'browser-mic' | 'attendee-bot' | 'none',
    then: () => void,
  ) {
    setBusy(mode);
    try {
      await startMeeting(sessionId, mode);
      then();
    } catch {
      // Keep the RM on a recoverable screen — the start did not happen.
      toast.error('Could not start the meeting. Please try again.');
      setBusy(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Start meeting"
        className="w-full max-w-md rounded-2xl border border-border bg-background p-6 shadow-xl outline-none"
      >
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Start meeting</h2>
            {/* Name the exact meeting BEFORE any state change (TASK-673 AC). */}
            <p className="text-sm text-foreground">{clientName}</p>
            <p className="text-xs text-muted-foreground">
              {meetingGoal?.trim() || 'No goal set for this meeting'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cancel start meeting"
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {isBot ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              A conference link is set, so a meeting bot captures the audio. Your microphone
              stays off to avoid recording the meeting twice.
            </p>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() =>
                choose('attendee-bot', () => {
                  if (meetingUrl) window.open(meetingUrl, '_blank', 'noopener');
                  addSpecs([
                    { component: 'LiveMeetingNow', props: { sessionId, clientName } },
                  ]);
                  navigate('/dashboard');
                  onClose();
                })
              }
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-kielis-navy px-4 py-3 text-sm font-medium text-white hover:bg-kielis-navy/90 disabled:opacity-50"
            >
              <Radio className="h-4 w-4" aria-hidden="true" />
              Join conference &amp; monitor the bot
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <button
              type="button"
              disabled={busy !== null}
              onClick={() =>
                choose('browser-mic', () => {
                  // Capture stays ON the canvas — summon the recorder tile, never route to a
                  // full page. The recorder still gates the mic behind explicit consent.
                  addSpecs([
                    { component: 'MeetingRecorderWidget', props: { sessionId, clientId, clientName } },
                  ]);
                  navigate('/dashboard');
                  onClose();
                })
              }
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-kielis-navy px-4 py-3 text-sm font-medium text-white hover:bg-kielis-navy/90 disabled:opacity-50"
            >
              <Mic className="h-4 w-4" aria-hidden="true" />
              Continue to consent &amp; recording
            </button>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() =>
                choose('none', () => {
                  addSpecs([
                    {
                      component: 'VoiceNoteWidget',
                      props: { clientId, clientName, sessionId },
                    },
                  ]);
                  navigate('/dashboard');
                  onClose();
                })
              }
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-border px-4 py-3 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
            >
              In person — don&apos;t record, dictate a summary after
            </button>
            <p className="text-xs text-muted-foreground">
              Nothing is recording yet. Continue to the recorder, confirm the client&apos;s
              consent, and then explicitly start the microphone.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CaptureModeChooser;
